<template>
  <div class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h3><i class="fa fa-mobile"></i> SMS Templates</h3>
        <button class="close-btn" @click="$emit('cancel')">
          <i class="fa fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <div class="search-section">
          <div class="search-input-container">
            <i class="fa fa-search"></i>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search SMS templates..."
              class="search-input"
              @input="filterTemplates"
              ref="searchInput"
            />
          </div>
        </div>
        
        <div class="template-list" v-if="filteredTemplates.length > 0">
          <div 
            v-for="template in filteredTemplates" 
            :key="template.id"
            class="template-item"
            :class="{ 'selected': selectedTemplate && selectedTemplate.id === template.id }"
            @click="selectTemplate(template)"
          >
            <div class="template-info">
              <div class="template-title">{{ template.title }}</div>
              <div class="template-preview">{{ template.content }}</div>
              <div class="template-meta">
                <span class="template-category" :class="`category-${template.category?.toLowerCase()}`">
                  {{ template.category }}
                </span>
                <span class="sms-length" :class="getSmsLengthClass(template.content)">
                  {{ template.content.length }} chars
                  <span v-if="template.content.length > 160">({{ Math.ceil(template.content.length / 160) }} SMS)</span>
                </span>
                <span class="template-usage" v-if="template.usage">
                  Used {{ template.usage }} times
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div v-else-if="loadingTemplates" class="loading-state">
          <i class="fa fa-spinner fa-spin"></i>
          <span>Loading SMS templates...</span>
        </div>
        
        <div v-else class="empty-state">
          <i class="fa fa-search"></i>
          <span>No SMS templates found</span>
        </div>
        
        <!-- Selected Template Preview -->
        <div v-if="selectedTemplate" class="selected-preview">
          <h4>Selected SMS Template:</h4>
          <div class="preview-card">
            <strong>{{ selectedTemplate.title }}</strong>
            <div class="template-content">{{ selectedTemplate.content }}</div>
            <!-- Shared SMS guidance: live credit counter + warn/block banner + tips. Catches a
                 legacy over-cap template before a direct send (the button below is gated on it). -->
            <SmsGuidance :text="selectedTemplate.content || ''" substitute-tags />
            <div class="template-actions">
              <span class="text-muted" v-pre><i class="fa fa-info-circle"></i> Variables like {{patient_first_name}} will be replaced automatically when sent</span>
            </div>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button
          class="btn btn-primary"
          @click="sendTemplate"
          :disabled="!selectedTemplate || selectedTemplateIsEmpty || (sendOption === 'direct' && selectedTemplateGuidance && selectedTemplateGuidance.blocked)"
        >
          <i class="fa fa-edit"></i>
          Select Template
        </button>
        <button class="btn btn-secondary" @click="$emit('cancel')">
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import mixin from '@/mixin.js'
import SmsGuidance from '@/components/SmsGuidance.vue'
import { getSmsGuidance } from '@/utils/smsGuidance'
import { withoutReviewRequestTemplates } from '@/utils/wikiTags'

export default {
  name: 'SmsTemplateSelectionModal',
  components: { SmsGuidance },
  mixins: [mixin],
  data() {
    return {
      searchQuery: '',
      selectedTemplate: null,
      filteredTemplates: [],
      loadingTemplates: false,
      sendOption: 'direct',
      wikiTemplates: []  // Will hold templates from wiki
    }
  },
  computed: {
    ...mapState('customer', ['siteID']),

    // Shared SMS guidance for the currently-selected template (template-aware: {{merge_tags}}
    // rendered with sample values). Drives the preview banner + the direct-send gate below.
    selectedTemplateGuidance() {
      if (!this.selectedTemplate) return null;
      return getSmsGuidance(this.selectedTemplate.content || '', { substituteTags: true });
    },

    // A template that renders to no text has nothing to send. getSmsGuidance only ever checks the
    // MAXIMUM (segments > carrier cap), so an empty template scores a clean 'ok' and sails through
    // every existing gate — that is how the 2026-08-04 blank template reached the send worker.
    selectedTemplateIsEmpty() {
      if (!this.selectedTemplate) return false;
      const content = this.selectedTemplate.content;
      return !content || String(content).trim() === '';
    },

    availableTemplates() {
      // Return wiki templates if available, otherwise fallback to hardcoded templates
      if (this.wikiTemplates && this.wikiTemplates.length > 0) {
        // Transform wiki templates to match the expected format
        return this.wikiTemplates.map((wiki, index) => {
          const parsed = this.parseEditorJsContent(wiki.Content)
          return {
            id: wiki.WikiPageID || index + 1000, // Use WikiPageID as id
            title: wiki.Name,
            content: parsed.text, // Parse EditorJS JSON to readable text with link placeholders
            linkMap: parsed.linkMap, // Map of placeholders → original HTML anchors
            rawContent: wiki.Content, // Keep raw content for backend processing if needed
            category: wiki.Tags || 'General',
            usage: 0, // Wiki doesn't track usage yet
            wikiPageID: wiki.WikiPageID
          }
        })
      }
      
      // FALLBACK: SMS-specific templates for dental practices
      return [
        {
          id: 1,
          title: 'Appointment Confirmation',
          content: 'Hi! This is a reminder that you have an appointment with us tomorrow at {time}. Please reply CONFIRM to confirm or call us if you need to reschedule.',
          category: 'Appointments',
          usage: 45
        },
        {
          id: 2,
          title: 'Appointment Reminder - Short',
          content: 'Appointment reminder: {date} at {time}. Please arrive 15 min early. Reply STOP to opt out.',
          category: 'Appointments',
          usage: 32
        },
        {
          id: 3,
          title: 'Post-Treatment Care',
          content: 'Thank you for your visit! Please follow the post-treatment instructions we provided. Call us with any concerns.',
          category: 'Follow-up',
          usage: 28
        },
        {
          id: 4,
          title: 'Insurance Verification',
          content: 'We need to verify your insurance before your next appointment. Please call us at your earliest convenience.',
          category: 'Insurance',
          usage: 15
        },
        {
          id: 5,
          title: 'Payment Reminder',
          content: 'Friendly reminder: You have an outstanding balance of ${amount}. Please contact us to discuss payment options.',
          category: 'Billing',
          usage: 12
        },
        {
          id: 6,
          title: 'Recall Appointment',
          content: 'Time for your dental checkup! Please call us to schedule. We have convenient times available.',
          category: 'Recall',
          usage: 38
        },
        {
          id: 7,
          title: 'Emergency Contact',
          content: 'For dental emergencies, call our emergency line at {emergency_phone}. For non-urgent matters, we\'ll respond during business hours.',
          category: 'Emergency',
          usage: 8
        },
        {
          id: 8,
          title: 'Welcome New Patient - Short',
          content: 'Welcome to our practice! Please arrive 15 min early for your first appointment to complete paperwork.',
          category: 'Welcome',
          usage: 22
        },
        {
          id: 9,
          title: 'Prescription Ready',
          content: 'Your prescription is ready for pickup. Office hours: Mon-Fri 8AM-5PM. Please bring valid ID.',
          category: 'Prescriptions',
          usage: 6
        },
        {
          id: 10,
          title: 'Lab Results Available',
          content: 'Your lab results are in. Please call to schedule a follow-up appointment to discuss the results.',
          category: 'Results',
          usage: 4
        },
        {
          id: 11,
          title: 'Running Late',
          content: 'We\'re running about {minutes} minutes behind schedule. Thank you for your patience!',
          category: 'Updates',
          usage: 18
        },
        {
          id: 12,
          title: 'Cancellation Available',
          content: 'We have an earlier appointment available on {date} at {time}. Reply YES if interested or call us.',
          category: 'Scheduling',
          usage: 14
        }
      ]
    }
  },
  async mounted() {
    // Load wiki templates with 'sms-template' tag
    await this.loadWikiTemplates()
    
    // Focus search input
    this.$nextTick(() => {
      this.$refs.searchInput?.focus()
    })
    
    this.filterTemplates()
  },
  methods: {
    async loadWikiTemplates() {
      try {
        this.loadingTemplates = true
        
        // Load SMS templates from wiki (same pattern as WikiSelectionModal)
        const result = await this.$store.dispatch('wiki/getWikiPages', {
          siteID: this.siteID,
          tags: 'sms-template'
        })
        
        // Result is an array where first element contains the wiki pages
        if (result && Array.isArray(result) && result.length > 0 && result[0]) {
          // Reviews templates stay out: only the Reviews send path can fill their {{review_link}}.
          this.wikiTemplates = withoutReviewRequestTemplates(result[0]).filter(wiki => wiki.IsSmsTemplate === 1 || wiki.IsSmsTemplate === '1')
          console.log(`Loaded ${this.wikiTemplates.length} SMS templates from wiki`)
        } else {
          console.warn('No SMS templates found in wiki, using fallback templates')
          this.wikiTemplates = []
        }
      } catch (error) {
        console.error('Error loading wiki SMS templates:', error)
        this.wikiTemplates = [] // Fall back to hardcoded templates
      } finally {
        this.loadingTemplates = false
      }
    },
    
    filterTemplates() {
      if (!this.searchQuery.trim()) {
        this.filteredTemplates = this.availableTemplates
        return
      }
      
      const query = this.searchQuery.toLowerCase()
      this.filteredTemplates = this.availableTemplates.filter(template => 
        template.title?.toLowerCase().includes(query) ||
        template.content?.toLowerCase().includes(query) ||
        template.category?.toLowerCase().includes(query)
      )
    },
    
    selectTemplate(template) {
      this.selectedTemplate = template
    },
    
    sendTemplate() {
      if (!this.selectedTemplate) return

      // An empty template has nothing to send on EITHER path — direct or select-to-edit — so it
      // is refused outright rather than loading a blank composer with no explanation.
      if (this.selectedTemplateIsEmpty) {
        this.$toast && this.$toast.error('This template has no message text. Open it in the Wiki and add the text first.')
        return
      }

      // Defense-in-depth for the direct-send path: never push a legacy over-carrier-limit
      // template straight out. Selecting-to-edit (sendOption !== 'direct') stays allowed so
      // the agent can trim it in the composer, which has its own guidance.
      if (this.sendOption === 'direct' && this.selectedTemplateGuidance && this.selectedTemplateGuidance.blocked) {
        const g = this.selectedTemplateGuidance
        this.$toast && this.$toast.error(`${g.title}. ${g.message}`)
        return
      }

      this.$emit('send', {
        id: this.selectedTemplate.id,
        title: this.selectedTemplate.title,
        content: this.selectedTemplate.content,
        linkMap: this.selectedTemplate.linkMap || {}, // Map of placeholders → original HTML anchors
        category: this.selectedTemplate.category,
        wikiPageID: this.selectedTemplate.wikiPageID, // Include wikiPageID for backend processing
        sendDirectly: this.sendOption === 'direct'
      })
    },
    
    /**
     * Parse EditorJS JSON content to readable text for SMS preview.
     * Converts <a> tags to visible placeholders ([Form: ...] or [Link: ...])
     * and builds a linkMap to restore them before sending.
     * @param {string} content - Raw EditorJS JSON string
     * @returns {{ text: string, linkMap: Object }} - Parsed text + link restoration map
     */
    parseEditorJsContent(content) {
      const emptyResult = { text: '', linkMap: {} }
      if (!content) return emptyResult

      try {
        // If it's already plain text (not JSON), return as-is
        if (typeof content === 'string' && !content.startsWith('{')) {
          return { text: content, linkMap: {} }
        }

        const parsed = typeof content === 'string' ? JSON.parse(content) : content

        if (!parsed.blocks || !Array.isArray(parsed.blocks)) {
          return { text: content, linkMap: {} }
        }

        let textParts = []
        let linkMap = {}

        /**
         * Convert <a> tags to [Form: text] or [Link: text] placeholders.
         * Form links have href containing "patient_link_for_form".
         * All other links are treated as external [Link: text].
         */
        const convertLinksToPlaceholders = (html) => {
          return html.replace(/<a\s+href="([^"]*)"[^>]*>(.*?)<\/a>/gi, (match, href, displayText) => {
            // Skip links with empty display text
            if (!displayText || displayText.trim() === '') return ''

            const cleanText = displayText.replace(/<[^>]*>/g, '').trim()
            let placeholder

            if (href.indexOf('patient_link_for_form') >= 0) {
              placeholder = `[Form: ${cleanText}]`
            } else {
              placeholder = `[Link: ${cleanText}]`
            }

            // Store the original HTML anchor for restoration before sending
            linkMap[placeholder] = match
            return placeholder
          })
        }

        const collectFromBlock = (block) => {
          if (!block.data) return

          // Handle paragraph blocks
          if (block.data.text) {
            // First convert links to placeholders, then strip remaining HTML
            let text = convertLinksToPlaceholders(block.data.text)
            text = text
              .replace(/<[^>]*>/g, '') // Remove remaining HTML tags (bold, italic, etc.)
              .replace(/&nbsp;/g, ' ')
              .replace(/&amp;/g, '&')
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>')
              .replace(/&quot;/g, '"')
              .trim()
            if (text) textParts.push(text)
          }

          // Handle list blocks
          if (block.data.items && Array.isArray(block.data.items)) {
            for (const item of block.data.items) {
              const itemText = typeof item === 'string' ? item : item.text
              if (itemText) {
                let text = convertLinksToPlaceholders(itemText)
                text = text
                  .replace(/<[^>]*>/g, '')
                  .replace(/&nbsp;/g, ' ')
                  .trim()
                if (text) textParts.push(`• ${text}`)
              }
            }
          }
        }

        // Skip header blocks for SMS (they're titles, not message content) — UNLESS they're all
        // the template has, in which case they ARE the message. Mirrors the backend renderer and
        // src/utils/smsTemplateText.js (PROD outage 2026-08-04): ten Tulip Tree templates hold
        // their whole patient-facing message in one header block, so a paragraphs-only read
        // rendered them blank in this picker while the backend now sends them fine.
        for (const block of parsed.blocks) {
          if (block.type === 'header') continue
          collectFromBlock(block)
        }
        if (textParts.length === 0) {
          for (const block of parsed.blocks) {
            if (block.type !== 'header') continue
            collectFromBlock(block)
          }
        }

        return { text: textParts.join('\n'), linkMap }
      } catch (e) {
        console.warn('Failed to parse EditorJS content:', e)
        if (typeof content === 'string') {
          return { text: content.replace(/[{}[\]"]/g, '').substring(0, 200), linkMap: {} }
        }
        return emptyResult
      }
    },
    
    getSmsLengthClass(content) {
      if (!content) return ''
      const length = content.length
      if (length <= 160) return 'good'
      if (length <= 320) return 'warning'
      return 'error'
    },
    
    handleOverlayClick() {
      this.$emit('cancel')
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 700px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  
  h3 {
    margin: 0;
    color: #333;
    font-size: 18px;
    
    .fa {
      margin-right: 8px;
      color: #1976d2;
    }
  }
  
  .close-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    color: #666;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background: #f5f5f5;
    }
  }
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.search-section {
  margin-bottom: 20px;
  
  .search-input-container {
    position: relative;
    
    .fa-search {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: #666;
    }
    
    .search-input {
      width: 100%;
      padding: 12px 12px 12px 40px;
      border: 1px solid #ddd;
      border-radius: 8px;
      font-size: 14px;
      outline: none;
      
      &:focus {
        border-color: #1976d2;
        box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
      }
    }
  }
}

.template-list {
  .template-item {
    padding: 16px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    margin-bottom: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      border-color: #1976d2;
      background: #f8f9ff;
    }
    
    &.selected {
      border-color: #1976d2;
      background: #e3f2fd;
    }
    
    .template-info {
      .template-title {
        font-weight: 600;
        color: #333;
        margin-bottom: 8px;
      }
      
      .template-preview {
        color: #666;
        font-size: 13px;
        line-height: 1.4;
        margin-bottom: 12px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      
      .template-meta {
        display: flex;
        gap: 12px;
        align-items: center;
        font-size: 12px;
        flex-wrap: wrap;
        
        .template-category {
          padding: 2px 8px;
          border-radius: 12px;
          font-weight: 500;
          text-transform: uppercase;
          font-size: 10px;
          
          &.category-appointments {
            background: #e3f2fd;
            color: #1976d2;
          }
          
          &.category-follow-up {
            background: #e8f5e8;
            color: #4caf50;
          }
          
          &.category-insurance {
            background: #fff3e0;
            color: #f57c00;
          }
          
          &.category-billing {
            background: #fce4ec;
            color: #c2185b;
          }
          
          &.category-recall {
            background: #f3e5f5;
            color: #9c27b0;
          }
          
          &.category-emergency {
            background: #ffebee;
            color: #f44336;
          }
          
          &.category-welcome {
            background: #e0f2f1;
            color: #00695c;
          }
          
          &.category-prescriptions {
            background: #e8eaf6;
            color: #3f51b5;
          }
          
          &.category-results {
            background: #fff8e1;
            color: #ff8f00;
          }
          
          &.category-updates {
            background: #e1f5fe;
            color: #0277bd;
          }
          
          &.category-scheduling {
            background: #f1f8e9;
            color: #558b2f;
          }
          
          // Default style
          &:not([class*="category-"]) {
            background: #f0f0f0;
            color: #666;
          }
        }
        
        .sms-length {
          font-weight: 500;
          
          &.good {
            color: #4caf50;
          }
          
          &.warning {
            color: #ff9800;
          }
          
          &.error {
            color: #f44336;
          }
        }
        
        .template-usage {
          color: #999;
        }
      }
    }
  }
}

.loading-state, .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #666;
  
  .fa {
    font-size: 24px;
    margin-bottom: 8px;
  }
}

.selected-preview {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
  
  h4 {
    margin: 0 0 12px 0;
    font-size: 14px;
    color: #666;
  }
  
  .preview-card {
    background: #f8f9ff;
    border: 1px solid #e3f2fd;
    border-radius: 8px;
    padding: 16px;
    
    strong {
      color: #1976d2;
      display: block;
      margin-bottom: 12px;
    }
    
    .template-content {
      background: white;
      border: 1px solid #e0e0e0;
      border-radius: 6px;
      padding: 12px;
      margin-bottom: 16px;
      font-size: 14px;
      line-height: 1.4;
      color: #333;
    }
    
    .sms-analysis {
      margin-bottom: 16px;
      
      .analysis-item {
        display: flex;
        justify-content: space-between;
        margin-bottom: 6px;
        font-size: 12px;
        
        .label {
          font-weight: 600;
          color: #666;
        }
        
        .value {
          &.good {
            color: #4caf50;
          }
          
          &.warning {
            color: #ff9800;
          }
          
          &.error {
            color: #f44336;
          }
        }
      }
    }
    
    .template-actions {
      display: flex;
      gap: 20px;
      
      .send-option {
        display: flex;
        align-items: center;
        cursor: pointer;
        font-size: 14px;
        
        input[type="radio"] {
          margin-right: 8px;
        }
        
        span {
          color: #666;
        }
        
        &:hover span {
          color: #333;
        }
      }
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid #e0e0e0;
  
  .btn {
    padding: 10px 20px;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s ease;
    
    &.btn-primary {
      background: #1976d2;
      color: white;
      
      &:hover:not(:disabled) {
        background: #1565c0;
      }
      
      &:disabled {
        background: #ccc;
        cursor: not-allowed;
      }
    }
    
    &.btn-secondary {
      background: #f5f5f5;
      color: #666;
      
      &:hover {
        background: #e0e0e0;
      }
    }
  }
}

// Mobile responsiveness
@media (max-width: 768px) {
  .modal-overlay {
    padding: 10px;
  }
  
  .modal-content {
    max-height: 90vh;
  }
  
  .modal-header, .modal-body, .modal-footer {
    padding: 16px;
  }
  
  .template-actions {
    flex-direction: column;
    gap: 12px !important;
  }
  
  .template-meta {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 6px !important;
  }
}
</style>
